/**
 * src/lib/i18n/translate.js - the pure lookup behind useT(), split out (TICKET-1189)
 * so a surface can translate in a FIXED locale (quote preview renders in the quote's
 * language, not the app's) without the React context.
 */

import en from './en.json';
import es from './es.json';

export const LOCALES = { en, es };
export const DEFAULT_LOCALE = 'en';

/**
 * Resolve a dot-path key like 'nav.focus' from a locale object.
 */
function resolve(obj, path) {
  if (typeof path !== 'string' || !path) return undefined;
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

/**
 * Replace {placeholder} tokens in a string.
 * t('focus.showCompleted', { count: 5 }) => "Show 5 completed"
 */
function interpolate(str, params) {
  if (!params || typeof str !== 'string') return str;
  return str.replace(/\{(\w+)\}/g, (_, key) => params[key] ?? `{${key}}`);
}

/**
 * Shared lookup: locale → English → params.defaultValue → raw key.
 * The defaultValue rung is load-bearing (TICKET-233): half the quote/e-sign
 * surface calls t(key, { defaultValue }) i18next-style; before this rung a
 * key missing from BOTH json files rendered as the raw key ("quoteSign.title")
 * on the customer-facing sign page.
 */
export function translate(locale, key, params) {
  const messages = LOCALES[locale] || LOCALES[DEFAULT_LOCALE];
  let value = resolve(messages, key);
  if (value === undefined) value = resolve(LOCALES[DEFAULT_LOCALE], key);
  if (value === undefined && params && typeof params.defaultValue === 'string') value = params.defaultValue;
  return interpolate(value !== undefined ? value : key, params);
}

/** A t() pinned to one locale, independent of the app language. */
export function tForLocale(locale) {
  return (key, params) => translate(locale, key, params);
}
